// i18n/index.tsx
// Internationalization context and hook.

import React, { createContext, useContext, useState, useCallback } from 'react';
import { en } from './en';
import { zh } from './zh';

export type Locale = 'en' | 'zh';

const translations = { en, zh } as Record<Locale, typeof en>;

interface I18nContextValue {
  locale: Locale;
  t: typeof en;
  setLocale: (locale: Locale) => void;
}

const I18nContext = createContext<I18nContextValue>({
  locale: 'zh',
  t: zh,
  setLocale: () => {},
});

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const saved = localStorage.getItem('locale') as Locale;
    // Default to Chinese: the dashboard is an operator console primarily used
    // by Chinese-speaking operators. English remains available via the EN/中文
    // toggle on /auto-trading; once a user picks a language we honour it.
    return saved === 'en' || saved === 'zh' ? saved : 'zh';
  });

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    localStorage.setItem('locale', l);
  }, []);

  return (
    <I18nContext.Provider value={{ locale, t: translations[locale], setLocale }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}
